import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Grid,
  TextField,
  Tooltip,
} from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Controller, FieldValues, useForm } from 'react-hook-form'
import DateFnsProvider from '../../../providers/DateFnsProvider'
import { SelectItem, getSelectItemOptionLabel, normalizeSelectItems, isSelectItemEqual } from '../../../types/SelectItem'
import {
  useAutoCompleteOptionsLazyQuery,
  useAutocompleteTitleOptionsLazyQuery,
  useContentAnalysisTagsOptionsQuery,
} from '@kistill/client/data-access'
import { ListViewContext } from '../../../contexts/ListViewContext'
import { FilterControls } from './FilterControls'
import { DateSearch } from '../../SearchBar/DateSearch'
import {
  handleOnSubmit,
  OnFilterType,
} from '../../../hooks/useHandleOnSubmitFilters'
import { getHiddenFilterFields } from '../filterConfig'
import { useClearFiltersOnColumnHide } from '../../../hooks/useClearFiltersOnColumnHide'
import { useFilterSync } from '../../../hooks/useFilterSync'

interface ContentAnalysisFilterProps {
  onFilter: OnFilterType
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  columnVisibility?: { [key: string]: boolean }
  initialFilterParams?: Record<string, unknown>
}

export function ContentAnalysisFilters({
  onFilter,
  selected,
  setSelected,
  columnVisibility = {},
  initialFilterParams,
}: ContentAnalysisFilterProps) {
  // Get hidden filter fields based on column visibility
  const hiddenFields = getHiddenFilterFields('contentAnalysis', columnVisibility)
  const { handleSubmit, watch, control, register, setValue, reset } = useForm({ shouldUnregister: true })
  const { showOnlySelected, updateShowOnlySelected } =
    useContext(ListViewContext)
  const [inputValueTitle, setInputValueTitle] = useState('')
  const [inputValueItem, setInputValueItem] = useState('')
  const [titleOptions, setTitleOptions] = useState<SelectItem[]>([])
  const [itemOptions, setItemOptions] = useState<SelectItem[]>([])

  const { data: tagsData } = useContentAnalysisTagsOptionsQuery()
  const tagOptions = tagsData?.contentAnalysisTagsOptions as SelectItem[]

  const [getTitleOptions] = useAutocompleteTitleOptionsLazyQuery({
    variables: { query: inputValueTitle },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.titleOptions) {
        setTitleOptions(data.titleOptions.slice()) // Use slice() to create a new array
      }
    },
  })

  const [getItemNumberOptions] = useAutoCompleteOptionsLazyQuery({
    variables: { query: inputValueItem },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.itemNumberOptions) {
        setItemOptions(
          data.itemNumberOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const onSubmit = useCallback(
    (data: FieldValues) => {
      handleOnSubmit(onFilter)(data)
    },
    [onFilter],
  )

  const { formStateRef, clearFilters, clearSpecificFields } = useFilterSync({
    initialFilterParams,
    reset,
    watch,
    onSubmit,
    getSyncKey: (normalized) =>
      normalized === null ? null : JSON.stringify(normalized),
    getClearSyncKey: () => 'contentAnalysis',
  })

  const handleInputChangeTitle = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueTitle(value)
  }

  const handleInputChangeItem = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueItem(value)
  }

  const deselectCheckboxChange = () => {
    setSelected(new Set())
    if (showOnlySelected) {
      setValue('selectedGuids', null)
      updateShowOnlySelected(!showOnlySelected)
      reset({})
    }
  }

  useEffect(() => {
    getItemNumberOptions() // Trigger the query to fetch item options
  }, [getItemNumberOptions])

  useEffect(() => {
    getTitleOptions({ variables: { query: inputValueTitle } }) // Trigger the query to fetch title options
  }, [getTitleOptions, inputValueTitle])

  // Clear filter values when their corresponding columns are hidden
  useClearFiltersOnColumnHide(
    'contentAnalysis',
    columnVisibility,
    setValue,
    clearSpecificFields,
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DateFnsProvider>
        <Grid container spacing={1}>
          {!hiddenFields.has('itemNumberSelectItem') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='itemNumberSelectItem'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    size='small'
                    options={normalizeSelectItems(itemOptions || [])}
                    getOptionLabel={getSelectItemOptionLabel}
                    isOptionEqualToValue={isSelectItemEqual}
                    onChange={(e, data) => {
                      const next = normalizeSelectItems(data)
                      onChange(next)
                      formStateRef.current = { ...formStateRef.current, itemNumberSelectItem: next }
                      setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                    }}
                    onInputChange={handleInputChangeItem}
                    value={value || []}
                    clearOnBlur={false}
                    renderOption={(props, option) => (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label='Númer eintaks' />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('titleSelectItem') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='titleSelectItem'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    size='small'
                    options={normalizeSelectItems(titleOptions || [])}
                    getOptionLabel={getSelectItemOptionLabel}
                    isOptionEqualToValue={isSelectItemEqual}
                    onChange={(e, data) => {
                      const next = normalizeSelectItems(data)
                      onChange(next)
                      formStateRef.current = { ...formStateRef.current, titleSelectItem: next }
                      setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                    }}
                    value={value || []}
                    inputValue={inputValueTitle}
                    onInputChange={handleInputChangeTitle}
                    clearOnBlur={false}
                    renderOption={(props, option) => (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label='Titill' />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('efnislysing') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='efnislysing'
                render={({ field }) => (
                  <Tooltip title='Leitaðu af mörgum orðum með því að setja , á milli orðanna.'>
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label='Efnislýsing'
                      fullWidth
                      size='small'
                    />
                  </Tooltip>
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('tagSelectItem') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='tagSelectItem'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    size='small'
                    options={normalizeSelectItems(tagOptions || [])}
                    getOptionLabel={getSelectItemOptionLabel}
                    isOptionEqualToValue={isSelectItemEqual}
                    onChange={(e, data) => {
                      const next = normalizeSelectItems(data)
                      onChange(next)
                      formStateRef.current = { ...formStateRef.current, tagSelectItem: next }
                      setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                    }}
                    value={value || []}
                    clearOnBlur={false}
                    renderOption={(props, option) => (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label='Tög' />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('dateFrom') && !hiddenFields.has('dateTo') && (
            <Grid item xs={12} md={4} sx={{ pr: 0.25 }}>
              <DateSearch
                register={register}
                control={control}
                dateFromLabel={'Ár frá'}
                dateToLabel={'Ár til'}
              />
            </Grid>
          )}
        </Grid>
      </DateFnsProvider>
      <FilterControls
        selected={selected}
        clearFilters={clearFilters}
        register={register}
        deselectCheckboxChange={deselectCheckboxChange}
      />
    </form>
  )
}
